import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useUpload } from "@/context/UploadContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/config/plans";
import { DocTypePicker } from "./DocTypePicker";

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>
);

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 rounded-xl bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const ConsentBox = ({ checked, onChange, className = "" }) => {
  const { L } = useApp();
  return (
  <label className={`flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50/60 p-4 text-sm sm:col-span-2 ${className}`}>
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid="consent-checkbox" />
    <span>
      <strong>{L("Persetujuan rakaman & notis AI.", "Recording consent & AI notice.")}</strong>{" "}
      {L("Saya mengesahkan semua peserta telah dimaklumkan dan bersetuju dirakam. Audio akan diproses oleh penyedia AI pihak ketiga (OpenAI & ElevenLabs melalui fal.ai) melalui pelayan kami untuk transkripsi dan penjanaan minit.",
        "I confirm all participants were informed and consent to being recorded. Audio will be processed by third-party AI providers (OpenAI & ElevenLabs via fal.ai) via our servers for transcription and minutes generation.")}
    </span>
  </label>
  );
};

export function useMeetingForm() {
  const { user } = useAuth();
  const initial = () => ({
    title: "", meeting_date: new Date().toISOString().slice(0, 10), doc_type: "official", participants: "", folder_id: "none",
    transcription_language: user?.transcription_language || "auto", output_language: user?.output_language || "ms",
    location: "", chair: "", visibility: "workspace", consent: false, transcription_mode: "detailed", glossary: "",
  });
  const [meta, setMeta] = useState(initial);
  return [meta, (k, v) => setMeta((m) => ({ ...m, [k]: v })), () => setMeta(initial())];
}

export const MeetingForm = ({ meta, set, hideConsent }) => {
  const { L } = useApp();
  const { workspace } = useAuth();
  const [folders, setFolders] = useState([]);
  useEffect(() => {
    if (workspace) api.get("/folders", { params: { workspace_id: workspace.id } }).then((r) => setFolders(r.data));
  }, [workspace]);
  const official = meta.doc_type === "official";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {isPaid(workspace) && <DocTypePicker value={meta.doc_type} onChange={(v) => set("doc_type", v)} />}
      <Field label={L("Tajuk mesyuarat", "Meeting title")}><Input value={meta.title} onChange={(e) => set("title", e.target.value)} placeholder={L("Kosongkan untuk tajuk automatik", "Leave blank for automatic title")} className="h-10 rounded-xl bg-white" data-testid="meeting-title-input" /></Field>
      <Field label={L("Tarikh", "Date")}><Input type="date" value={meta.meeting_date} onChange={(e) => set("meeting_date", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="meeting-date-input" /></Field>
      <Field label={L("Folder", "Folder")}><Pick value={meta.folder_id} onChange={(v) => set("folder_id", v)} testId="meeting-folder-select" options={[["none", L("Tiada folder", "No folder")], ...folders.map((f) => [f.id, f.name])]} /></Field>
      <Field label={L("Bahasa transkripsi (pertuturan)", "Transcription language (speech)")}><Pick value={meta.transcription_language} onChange={(v) => set("transcription_language", v)} testId="transcription-language-select" options={[["auto", L("Auto / campuran BM-EN", "Auto / mixed BM-EN")], ...LANGS.map(([k, ms, en]) => [k, L(ms, en)])]} /></Field>
      {isPaid(workspace) && <Field label={L("Bahasa hasil (minit & ringkasan)", "Output language (minutes & summary)")}><Pick value={meta.output_language} onChange={(v) => set("output_language", v)} testId="output-language-select" options={LANGS.map(([k, ms, en]) => [k, L(ms, en)])} /></Field>}
      <Field label={L("Peserta (pisahkan dengan koma)", "Participants (comma separated)")}><Input value={meta.participants} onChange={(e) => set("participants", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="meeting-participants-input" /></Field>
      <Field label={L("Keterlihatan", "Visibility")}><Pick value={meta.visibility} onChange={(v) => set("visibility", v)} testId="meeting-visibility-select" options={[["workspace", L("Semua ahli workspace", "All workspace members")], ["private", L("Peribadi (saya & admin)", "Private (me & admins)")]]} /></Field>
      <Field label={L("Mod transkripsi", "Transcription mode")}><Pick value={meta.transcription_mode} onChange={(v) => set("transcription_mode", v)} testId="transcription-mode-select" options={[["detailed", L("Terperinci (disyorkan)", "Detailed (recommended)")], ["fast", L("Pantas (asas)", "Fast (basic)")]]} /></Field>
      <Field label={L("Glosari / istilah khas (pilihan)", "Glossary / special terms (optional)")}><Input value={meta.glossary} onChange={(e) => set("glossary", e.target.value)} maxLength={500} placeholder={L("cth. MinitAI, KPI, Jabatan Kewangan", "e.g. MinitAI, KPI, Finance Dept")} className="h-10 rounded-xl bg-white" data-testid="meeting-glossary-input" /></Field>
      {official && (
        <>
          <Field label={L("Tempat", "Venue")}><Input value={meta.location} onChange={(e) => set("location", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="meeting-location-input" /></Field>
          <Field label={L("Pengerusi", "Chairperson")}><Input value={meta.chair} onChange={(e) => set("chair", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="meeting-chair-input" /></Field>
        </>
      )}
      {!hideConsent && <ConsentBox checked={meta.consent} onChange={(v) => set("consent", v)} />}
    </div>
  );
};

export function useSubmitMeeting() {
  const { workspace } = useAuth();
  const { L } = useApp();
  const { start, busy } = useUpload();
  const submit = async (blob, filename, meta, source) => {
    if (!meta.consent) return toast.error(L("Sila tandakan persetujuan rakaman.", "Please tick the recording consent.")), false;
    const m = Object.fromEntries(Object.entries(meta).map(([k, v]) => [k, k === "consent" ? !!v : k === "folder_id" && v === "none" ? "" : String(v ?? "")]));
    return start({ blob, filename, workspaceId: workspace.id, meta: { ...m, source } });
  };
  return { submit, busy, bar: null };
}
